import { Component, Element, h, Host, Prop, Watch } from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import { CmsLink } from '../../types/global';
import {
  createAbsoutePathIfRelative,
  DEFAULT_TRACKING_DELAY,
  delayPageChange,
} from '../../global/utility/tracking-helpers';
import { renderLink } from '../../global/utility/link-renderer';

interface UiCmsLinkListBody {
  alignment: 'vertical' | 'horizontal';
  links: CmsLink[];
  columns: number;
  // Optional style for all links. This will override the style of each link.
  style?: 'link' | 'button' | 'anchor' | 'tags';
  headline?: string;
}

@Component({
  tag: 'ui-cms-link-list',
  styleUrl: 'ui-cms-link-list.scss',
})
export class UiCmsLinkList {
  @Element() el: HTMLUiCmsLinkListElement;

  @Prop({ mutable: true }) body: UiCmsLinkListBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop() inverted: boolean;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  private organizedColumns: CmsLink[][];

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.organizedColumns = this.organizeLinks(this.setLinkStyles());
    this.el.style.setProperty('--columns', this.body.columns?.toString());
  }

  private onLinkClick(event: Event, link: CmsLink, linkElement?: HTMLElement): void {
    delayPageChange(
      DEFAULT_TRACKING_DELAY,
      event,
      (linkElement as HTMLAnchorElement) || (event.currentTarget as HTMLAnchorElement)
    );

    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName: 'Click Link in List',
        eventType: 'Action',
        eventCaller: this.el,
      },
      attributes: {
        elementType: 'Link',
        elementLabel: link.title,
        target: createAbsoutePathIfRelative(link.src),
      },
      category: {
        area: 'Content',
      },
    };

    window.ui?.services?.tracking?.track(trackingEvent);
  }

  private organizeLinks(links: CmsLink[]): CmsLink[][] {
    const { columns } = this.body;
    if (columns > 0) {
      return this.splitLinksToColumns(links, columns);
    }
    return [links];
  }

  /** If a style is set for the link list, apply it to all links. */
  private setLinkStyles(): CmsLink[] {
    const { style, links } = this.body;
    return links.map((link) => {
      const linkCopy = { ...link };
      if (style === 'button') linkCopy.buttonVariant = 'primary';
      if (style === 'link') linkCopy.buttonVariant = undefined;
      return linkCopy;
    });
  }

  private splitLinksToColumns(links: CmsLink[], columns?: number): CmsLink[][] {
    const linksPerColumn: number = Math.ceil(links.length / columns);
    const organizedColumns: CmsLink[][] = [];

    links.forEach((_, index) => {
      const linksForColumn: CmsLink[] = links.slice(index * linksPerColumn, (index + 1) * linksPerColumn);

      if (linksForColumn.length) {
        organizedColumns.push(linksForColumn);
      }
    });

    return organizedColumns;
  }

  render() {
    return (
      <Host>
        {this.body.headline && (
          <h4 class={{ 'sd-headline sd-headline--size-base': true, 'sd-headline--inverted': this.inverted }}>
            {this.body.headline}
          </h4>
        )}
        <div class="wrapper">
          {this.organizedColumns.map((column) => (
            <ul
              class={{
                'link-list': true,
                'link-list--horizontal': this.body.alignment === 'horizontal',
              }}
            >
              {column.map((link: CmsLink) => (
                <li>{renderLink(link, this.inverted, this.body.style, this.onLinkClick.bind(this))}</li>
              ))}
            </ul>
          ))}
        </div>
      </Host>
    );
  }
}
